import React from 'react';
import { Typography } from '@/app/common/Typography';
import { cn } from '@/app/lib/utils';
import type { TextLink, LegalLink } from '../interfaces';
import Link from 'next/link';
import { footerVariants } from '@workspace/widgets/Footer/footer.variants';
import { shouldOpenInNewTab } from '../lib/footer.utils';

export interface FooterBottomProps {
  copyrightText: string;
  additionalText?: TextLink;
  legalLinks?: LegalLink[];
  className?: string;
}

export const FooterBottom = ({ copyrightText, additionalText, legalLinks = [], className = '' }: FooterBottomProps) => {
  const { footerBottom, footerBottomContainer, footerBottomContent, footerBottomText } = footerVariants();
  const textColor = footerBottomText();
  const getHref = (link?: any) => {
    if (!link) return undefined;

    switch (link.type) {
      case 'internal':
      case 'external':
        return link.url;
      case 'anchor':
        return link.anchor ? `#${link.anchor}` : undefined;
      default:
        return link.url || link.href;
    }
  };

  const renderAdditionalText = () => {
    if (!additionalText) return null;

    const href = getHref(additionalText.link);
    const openInNewTab = shouldOpenInNewTab(additionalText.link);
    const className = cn(textColor, '!text-sm', additionalText.isBold && 'font-semibold', href && 'cursor-pointer');

    if (href) {
      return (
        <Link
          href={href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className={className}
        >
          {additionalText.text}
        </Link>
      );
    }

    return (
      <Typography variant="bodyXs" className={className}>
        {additionalText.text}
      </Typography>
    );
  };

  return (
    <div className={cn('font-body', footerBottom(), className)}>
      <div className={cn('wrapper', footerBottomContainer())}>
        <div className={cn('flex flex-col items-center justify-between md:flex-row', footerBottomContent(), textColor)}>
          <div className="text-center md:text-left font-bold md:pb-0">
            <Typography
              variant="bodyXs"
              className={cn('block md:inline mb-2.5 md:mb-0 font-semibold !text-sm', textColor)}
            >
              {copyrightText.split('|')[0]}
            </Typography>
            {copyrightText.includes('|') && (
              <>
                <Typography variant="bodyXs" className={cn('hidden md:inline !text-sm', textColor)}>
                  |
                </Typography>
                <Typography variant="bodyXs" className={cn('block md:inline font-semibold !text-sm', textColor)}>
                  {copyrightText.split('|')[1]}
                </Typography>
              </>
            )}
          </div>
          <div className="flex flex-col items-center md:items-end gap-xs">
            <div>{renderAdditionalText()}</div>
            <div className="flex items-center gap-2xs">
              {legalLinks.map((link, index) => {
                const hasValidHref = Boolean(link.url && link.url !== '#');
                const openInNewTab = shouldOpenInNewTab(link);
                const legalLinkText = (
                  <Typography
                    variant="link"
                    className={cn('hover:underline text-sm !font-normal', textColor, '!no-underline')}
                  >
                    {link.label}
                  </Typography>
                );

                return (
                  <React.Fragment key={index}>
                    {hasValidHref ? (
                      <Link
                        href={link.url}
                        target={openInNewTab ? '_blank' : undefined}
                        rel={openInNewTab ? 'noopener noreferrer' : undefined}
                      >
                        {legalLinkText}
                      </Link>
                    ) : (
                      legalLinkText
                    )}
                    {index < legalLinks.length - 1 && (
                      <Typography variant="bodyXs" className={cn(textColor, 'text-sm')}>
                        |
                      </Typography>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
